import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Card,
    Colors,
    Heading,
    Icon,
    SelectFilled,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LazyTranslationGroupAccordion } from './LazyTranslationGroupAccordion';
import {
    PerformanceScoreLevelsTranslationAccordion,
    type PerformanceScoreLevelTranslationChange,
} from './PerformanceScoreLevelsTranslationAccordion';
import { ProficiencyLevelsTranslationAccordion } from './ProficiencyLevelsTranslationAccordion';
import {
    ProfilesTranslationAccordion,
    type ProfileTranslationChange,
} from './ProfilesTranslationAccordion';

import type {
    CompetencyTranslationGroup,
    TranslationFieldPath,
} from '@/types/competencies/CompetencyTranslation';
import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CompetencyProfile } from '@/types/competency/CompetencyProfile';
import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';
import type { LocalizedText } from '@/utils/localization';

import { useGetProficiencyLevels, useUpdateProficiencyLevels } from '@/api/competency-services';
import {
    useGetCompetencyProfiles,
    useUpdateCompetencyProfile,
} from '@/api/competency-services/competency-profile';
import {
    useGetPerformanceScoreLevels,
    useReplacePerformanceScoreLevels,
} from '@/api/competency-services/performance-score-level';
import {
    useGetCompetencyTranslationGroupSummaries,
    useUpdateCompetencyTranslations,
} from '@/api/competency-services/translation';
import OverlayLoader from '@/components/OverlayLoader/OverlayLoader';
import { CompetencyTranslationFieldKind } from '@/constants/competencies/CompetencyTranslationFieldKind';
import { useAmelioTranslations, useSelectedAppLanguage } from '@/hooks';
import { useCompanyAvailableLanguages } from '@/hooks/company/useCompanyAvailableLanguages';
import { useError } from '@/hooks/utils/useError';
import {
    hasDuplicateCompetencyNameInGroup,
    isNameConflictError,
} from '@/pages/Competencies/validation';
import { showErrorToast, showSuccessToast } from '@/plugins/Toast';
import {
    collectModifiedTranslations,
    computeTranslationProgress,
    toCompetencyTranslationChanges,
} from '@/utils/competencies';
import { getLanguageDisplayName } from '@/utils/localization';

// True when two localized dictionaries differ in ANY language (union of keys), so an edit
// made in one language is still detected after the displayed target language changes.
const localizedTextChanged = (
    before: LocalizedText | undefined,
    after: LocalizedText | undefined,
): boolean => {
    const languages = new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]);
    for (const language of languages) {
        if ((before?.[language] ?? '') !== (after?.[language] ?? '')) {
            return true;
        }
    }
    return false;
};

export const CompetencyTranslationsTab = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const sourceLanguage = useSelectedAppLanguage();

    const { data: groupSummaries, isLoading } = useGetCompetencyTranslationGroupSummaries();
    const { mutateAsync: updateTranslations, isPending: isSaving } =
        useUpdateCompetencyTranslations();

    const { data: levelsData } = useGetProficiencyLevels();
    const { mutateAsync: updateLevels, isPending: isSavingLevels } = useUpdateProficiencyLevels();

    const { data: profilesData } = useGetCompetencyProfiles();
    const { mutateAsync: updateProfile, isPending: isSavingProfiles } =
        useUpdateCompetencyProfile();

    const { data: scoreLevelsData } = useGetPerformanceScoreLevels();
    const { mutateAsync: replaceScoreLevels, isPending: isSavingScoreLevels } =
        useReplacePerformanceScoreLevels();

    // Lazy-loaded groups: baseline = last loaded/saved copy; groups = the edited working copy.
    // Only groups that have been expanded are stored here.
    const [baseline, setBaseline] = useState<Map<string, CompetencyTranslationGroup>>(new Map());
    const [groups, setGroups] = useState<Map<string, CompetencyTranslationGroup>>(new Map());

    const [levelBaseline, setLevelBaseline] = useState<ProficiencyLevel[]>([]);
    const [levels, setLevels] = useState<ProficiencyLevel[]>([]);

    const [profileBaseline, setProfileBaseline] = useState<CompetencyProfile[]>([]);
    const [profiles, setProfiles] = useState<CompetencyProfile[]>([]);

    const [scoreLevelBaseline, setScoreLevelBaseline] = useState<PerformanceScoreLevel[]>([]);
    const [scoreLevels, setScoreLevels] = useState<PerformanceScoreLevel[]>([]);

    // Mirror unsaved-edit state (assigned each render, below) so a background refetch never
    // overwrites in-progress work; server data is only adopted while there are no local edits.
    const hasLevelEdits = useRef(false);
    const hasProfileEdits = useRef(false);
    const hasScoreLevelEdits = useRef(false);

    // Groups are adopted per group, never gated on whether OTHER groups have edits: a group
    // expanded after the user started translating elsewhere must still load. A group already
    // in the working copy is left untouched so a refetch never overwrites in-progress work.
    const handleGroupLoaded = useCallback((group: CompetencyTranslationGroup) => {
        setBaseline((prev) => (prev.has(group.id) ? prev : new Map(prev).set(group.id, group)));
        setGroups((prev) => (prev.has(group.id) ? prev : new Map(prev).set(group.id, group)));
    }, []);

    useEffect(() => {
        if (levelsData && !hasLevelEdits.current) {
            setLevelBaseline(levelsData);
            setLevels(levelsData);
        }
    }, [levelsData]);

    useEffect(() => {
        if (profilesData && !hasProfileEdits.current) {
            setProfileBaseline(profilesData);
            setProfiles(profilesData);
        }
    }, [profilesData]);

    useEffect(() => {
        if (scoreLevelsData && !hasScoreLevelEdits.current) {
            setScoreLevelBaseline(scoreLevelsData);
            setScoreLevels(scoreLevelsData);
        }
    }, [scoreLevelsData]);

    const labelProgressFor = (texts: { [language: string]: string }[], language: string) => {
        const translated = texts.filter((text) => (text[language] ?? '').trim().length > 0).length;
        return { translated, total: texts.length };
    };

    const levelProgressFor = (list: ProficiencyLevel[], language: string) =>
        labelProgressFor(
            list.map((level) => level.label),
            language,
        );

    const profileProgressFor = (list: CompetencyProfile[], language: string) => {
        const texts = list.flatMap((profile) => [profile.name, profile.description]);
        return labelProgressFor(texts, language);
    };

    const scoreLevelProgressFor = (list: PerformanceScoreLevel[], language: string) => {
        const texts = list.flatMap((level) => [level.label, level.description]);
        return labelProgressFor(texts, language);
    };

    const availableLanguages = useCompanyAvailableLanguages();

    const loadedGroupsArray = useMemo(() => [...groups.values()], [groups]);

    const targetLanguageOptions = useMemo(() => {
        const languages = new Set(availableLanguages);
        languages.delete(sourceLanguage);

        return [...languages].map((language) => {
            const groupProgress = (groupSummaries ?? []).reduce(
                (accumulator, summary) => {
                    const loadedGroup = groups.get(summary.id);
                    const progress = loadedGroup
                        ? computeTranslationProgress([loadedGroup], language)
                        : {
                              translated: summary.translationProgress.translated[language] ?? 0,
                              total: summary.translationProgress.total,
                          };
                    return {
                        translated: accumulator.translated + progress.translated,
                        total: accumulator.total + progress.total,
                    };
                },
                { translated: 0, total: 0 },
            );
            const levelProgress = levelProgressFor(levels, language);
            const scoreLevelProgress = scoreLevelProgressFor(scoreLevels, language);
            const profileProgress = profileProgressFor(profiles, language);
            const languageProgress = {
                translated:
                    groupProgress.translated +
                    levelProgress.translated +
                    scoreLevelProgress.translated +
                    profileProgress.translated,
                total:
                    groupProgress.total +
                    levelProgress.total +
                    scoreLevelProgress.total +
                    profileProgress.total,
            };
            const percent =
                languageProgress.total > 0
                    ? Math.round((languageProgress.translated / languageProgress.total) * 100)
                    : 0;

            return {
                label: `${getLanguageDisplayName(language)} ${percent}%`,
                value: language,
                progress: languageProgress,
            };
        });
    }, [availableLanguages, groupSummaries, groups, levels, profiles, scoreLevels, sourceLanguage]);

    const [targetLanguage, setTargetLanguage] = useState(targetLanguageOptions[0]?.value ?? 'en');

    // Keep the target valid: fall back to the first option if it matches the source or is gone.
    useEffect(() => {
        const optionValues = targetLanguageOptions.map((option) => option.value);
        if (optionValues.length > 0 && !optionValues.includes(targetLanguage)) {
            setTargetLanguage(optionValues[0]);
        }
    }, [targetLanguageOptions, targetLanguage]);

    const progress = useMemo(
        () =>
            targetLanguageOptions.find((option) => option.value === targetLanguage)?.progress ?? {
                translated: 0,
                total: 0,
            },
        [targetLanguageOptions, targetLanguage],
    );

    const percentTranslated =
        progress.total > 0 ? Math.round((progress.translated / progress.total) * 100) : 0;

    const baselineArray = useMemo(() => [...baseline.values()], [baseline]);

    const modifiedTranslations = useMemo(
        () => collectModifiedTranslations(baselineArray, loadedGroupsArray),
        [baselineArray, loadedGroupsArray],
    );

    // Diffed across ALL languages (like groups): an edit in language A survives switching to B.
    const modifiedLevelCount = useMemo(
        () =>
            levels.filter((level, index) =>
                localizedTextChanged(levelBaseline[index]?.label, level.label),
            ).length,
        [levels, levelBaseline],
    );

    const modifiedProfiles = useMemo(
        () =>
            profiles.filter((profile) => {
                const base = profileBaseline.find((item) => item.id === profile.id);
                return (
                    localizedTextChanged(base?.name, profile.name) ||
                    localizedTextChanged(base?.description, profile.description)
                );
            }),
        [profiles, profileBaseline],
    );

    const modifiedScoreLevels = useMemo(
        () =>
            scoreLevels.filter((level) => {
                const base = scoreLevelBaseline.find((item) => item.id === level.id);
                return (
                    localizedTextChanged(base?.label, level.label) ||
                    localizedTextChanged(base?.description, level.description)
                );
            }),
        [scoreLevels, scoreLevelBaseline],
    );

    const modifiedCount =
        modifiedTranslations.length +
        modifiedLevelCount +
        modifiedProfiles.length +
        modifiedScoreLevels.length;
    const isDirty = modifiedCount > 0;

    hasLevelEdits.current = modifiedLevelCount > 0;
    hasProfileEdits.current = modifiedProfiles.length > 0;
    hasScoreLevelEdits.current = modifiedScoreLevels.length > 0;

    const onSave = async () => {
        const duplicateGroup = loadedGroupsArray.find((group) =>
            hasDuplicateCompetencyNameInGroup(group.competencies),
        );

        if (duplicateGroup) {
            showErrorToast({ message: t('competencies.validation.competencyNameDuplicate') });
            return;
        }

        try {
            // Only modified fields are sent, not the full dataset.
            if (modifiedTranslations.length > 0) {
                const saved = await updateTranslations(
                    toCompetencyTranslationChanges(modifiedTranslations),
                );
                const newBaseline = new Map<string, CompetencyTranslationGroup>();
                const newGroups = new Map<string, CompetencyTranslationGroup>();
                for (const group of saved) {
                    newBaseline.set(group.id, group);
                    newGroups.set(group.id, group);
                }
                setBaseline(newBaseline);
                setGroups(newGroups);
            }

            if (modifiedLevelCount > 0) {
                const savedLevels = await updateLevels(levels);
                setLevelBaseline(savedLevels);
                setLevels(savedLevels);
            }

            if (modifiedScoreLevels.length > 0) {
                const savedScoreLevels = await replaceScoreLevels({ levels: scoreLevels });
                setScoreLevelBaseline(savedScoreLevels);
                setScoreLevels(savedScoreLevels);
            }

            // Profiles have no shared translation kind, so each is saved via its per-profile mutation.
            if (modifiedProfiles.length > 0) {
                await Promise.all(
                    modifiedProfiles.map((profile) =>
                        updateProfile({
                            id: profile.id,
                            payload: {
                                name: profile.name,
                                description: profile.description,
                                sortOrder: profile.sortOrder,
                                items: profile.items,
                            },
                        }),
                    ),
                );
                setProfileBaseline(profiles);
            }

            showSuccessToast({ message: t('competenciesTranslations.saveSuccess') });
        } catch (error) {
            if (isNameConflictError(error)) {
                showErrorToast({ message: t('competencies.validation.competencyNameDuplicate') });
                return;
            }

            handleError({ error });
        }
    };

    const handleLevelChange = (index: number, value: string) => {
        setLevels((previous) =>
            previous.map((level, levelIndex) =>
                levelIndex === index
                    ? { ...level, label: { ...level.label, [targetLanguage]: value } }
                    : level,
            ),
        );
    };

    const handleProfileChange = (change: ProfileTranslationChange) => {
        setProfiles((previous) =>
            previous.map((profile) =>
                profile.id === change.profileId
                    ? {
                          ...profile,
                          [change.field]: {
                              ...profile[change.field],
                              [targetLanguage]: change.value,
                          },
                      }
                    : profile,
            ),
        );
    };

    const handleScoreLevelChange = (change: PerformanceScoreLevelTranslationChange) => {
        setScoreLevels((previous) =>
            previous.map((level) =>
                level.id === change.levelId
                    ? {
                          ...level,
                          [change.field]: {
                              ...level[change.field],
                              [targetLanguage]: change.value,
                          },
                      }
                    : level,
            ),
        );
    };

    const handleChange = useCallback(
        (path: TranslationFieldPath, value: string) => {
            setGroups((previous) => {
                const group = previous.get(path.groupId);
                if (!group) {
                    return previous;
                }

                let updatedGroup: CompetencyTranslationGroup;

                if (path.kind === CompetencyTranslationFieldKind.GROUP_NAME) {
                    updatedGroup = {
                        ...group,
                        name: { ...group.name, [targetLanguage]: value },
                    };
                } else if (path.kind === CompetencyTranslationFieldKind.GROUP_DESCRIPTION) {
                    updatedGroup = {
                        ...group,
                        description: { ...group.description, [targetLanguage]: value },
                    };
                } else {
                    updatedGroup = {
                        ...group,
                        competencies: group.competencies.map((competency) => {
                            if (competency.id !== path.competencyId) {
                                return competency;
                            }

                            if (path.kind === CompetencyTranslationFieldKind.COMPETENCY_NAME) {
                                return {
                                    ...competency,
                                    name: { ...competency.name, [targetLanguage]: value },
                                };
                            }

                            if (
                                path.kind === CompetencyTranslationFieldKind.COMPETENCY_DESCRIPTION
                            ) {
                                return {
                                    ...competency,
                                    description: {
                                        ...(competency.description ?? {}),
                                        [targetLanguage]: value,
                                    },
                                };
                            }

                            return {
                                ...competency,
                                questions: competency.questions.map((question) => {
                                    if (question.id !== path.questionId) {
                                        return question;
                                    }

                                    if (
                                        path.kind === CompetencyTranslationFieldKind.QUESTION_LABEL
                                    ) {
                                        return {
                                            ...question,
                                            label: { ...question.label, [targetLanguage]: value },
                                        };
                                    }

                                    return {
                                        ...question,
                                        options: question.options.map((option) =>
                                            option.id === path.optionId
                                                ? {
                                                      ...option,
                                                      label: {
                                                          ...option.label,
                                                          [targetLanguage]: value,
                                                      },
                                                  }
                                                : option,
                                        ),
                                    };
                                }),
                            };
                        }),
                    };
                }

                return new Map(previous).set(path.groupId, updatedGroup);
            });
        },
        [targetLanguage],
    );

    if (isLoading) {
        return (
            <div className={'mt-8 flex justify-center'}>
                <OverlayLoader isLoading={true} />
            </div>
        );
    }

    return (
        <Card
            borderRadius={'3xl'}
            padding={'none'}
            className={'mt-8 max-md:p-6 md:p-[32px]'}
        >
            <div className={'flex flex-wrap items-center justify-between gap-4'}>
                <div className={'flex items-center gap-3'}>
                    <Icon
                        name={'location-global'}
                        color={Colors.PRIMARY}
                        className={'h-[20px] w-[20px] shrink-0'}
                    />

                    <Heading
                        level={4}
                        weight={'semibold'}
                    >
                        {t('competenciesTranslations.title')}
                    </Heading>
                </div>

                <div className={'flex flex-wrap items-center gap-3'}>
                    {targetLanguageOptions?.length ? (
                        <SelectFilled
                            value={targetLanguage}
                            onChange={setTargetLanguage}
                            options={targetLanguageOptions}
                            variant={'white'}
                            triggerBorderClassName={'border border-gray-200'}
                        />
                    ) : null}

                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div className={'relative inline-block'}>
                                    <ButtonSecondary
                                        disabled
                                        size={'md'}
                                        aria-disabled={'true'}
                                        leftIcon={
                                            <Icon
                                                name={'support-magic-star'}
                                                color={Colors.BASE_300}
                                                className={'mr-2'}
                                            />
                                        }
                                    >
                                        {t('competenciesTranslations.autoTranslate')}
                                    </ButtonSecondary>
                                </div>
                            </TooltipTrigger>

                            <TooltipContent side={'top'}>
                                <p>{t('competenciesTranslations.comingSoonTooltip')}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>

            <div className={'mt-8 flex items-center gap-4'}>
                <div className={'bg-base-50 h-2 flex-1 overflow-hidden rounded-full'}>
                    <div
                        className={'h-full'}
                        style={{
                            width: `${percentTranslated}%`,
                            backgroundImage:
                                'linear-gradient(to right, var(--color-primary), var(--color-secondary))',
                        }}
                    />
                </div>

                <Text
                    size={'small'}
                    color={Colors.BASE_400}
                    className={'text-caption whitespace-nowrap'}
                    data-testid={'translations-global-progress'}
                >
                    {t('competenciesTranslations.fieldsTranslated', {
                        translated: progress.translated,
                        total: progress.total,
                    })}
                </Text>
            </div>

            <div className={'mt-8 flex flex-col gap-6'}>
                {levels.length > 0 && (
                    <ProficiencyLevelsTranslationAccordion
                        levels={levels}
                        sourceLanguage={sourceLanguage}
                        targetLanguage={targetLanguage}
                        onChange={handleLevelChange}
                    />
                )}

                {scoreLevels.length > 0 && (
                    <PerformanceScoreLevelsTranslationAccordion
                        levels={scoreLevels}
                        sourceLanguage={sourceLanguage}
                        targetLanguage={targetLanguage}
                        onChange={handleScoreLevelChange}
                    />
                )}

                {profiles.length > 0 && (
                    <ProfilesTranslationAccordion
                        profiles={profiles}
                        sourceLanguage={sourceLanguage}
                        targetLanguage={targetLanguage}
                        onChange={handleProfileChange}
                    />
                )}

                {groupSummaries?.map((summary) => (
                    <LazyTranslationGroupAccordion
                        key={summary.id}
                        groupSummary={summary}
                        loadedGroup={groups.get(summary.id)}
                        sourceLanguage={sourceLanguage}
                        targetLanguage={targetLanguage}
                        onChange={handleChange}
                        onGroupLoaded={handleGroupLoaded}
                    />
                ))}
            </div>

            <div className={'sticky bottom-6 z-10 mt-6 flex justify-end'}>
                <ButtonGradientPrimary
                    onClick={onSave}
                    disabled={
                        !isDirty ||
                        isSaving ||
                        isSavingLevels ||
                        isSavingScoreLevels ||
                        isSavingProfiles
                    }
                    loading={isSaving || isSavingLevels || isSavingScoreLevels || isSavingProfiles}
                    dataTestId={'translations-save'}
                    shape={'pill'}
                    className={'h-[44px]'}
                >
                    {isDirty
                        ? t('competenciesTranslations.saveWithCount', {
                              count: modifiedCount,
                          })
                        : t('common.save')}
                </ButtonGradientPrimary>
            </div>
        </Card>
    );
};
